import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * Custom hook to track full URL changes including path, query parameters, and fragments
 * Returns the complete URL string for dependency tracking
 * 
 * Note: We use window.location for query params and hash instead of useSearchParams
 * to avoid Suspense boundary requirements
 *
 * Optimized: Uses event listeners for hash and popstate changes
 */
export const useFullUrl = () => {
  const location = useLocation();
  const [fullUrl, setFullUrl] = useState('');

  // Update full URL when pathname, search, or hash changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Build full URL from pathname, search, and hash
    const search = window.location.search; // includes '?'
    const hash = window.location.hash; // includes '#'
    const url = `${location.pathname}${search}${hash}`;

    setFullUrl(url);
  }, [location.pathname]);

  // Listen to hash and popstate changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleUrlChange = () => {
      const search = window.location.search;
      const hash = window.location.hash;
      const url = `${location.pathname}${search}${hash}`;
      setFullUrl(url);
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleUrlChange);
    
    // Listen for popstate (back/forward button and browser history)
    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [location.pathname]);

  return fullUrl;
};

export default useFullUrl;
